/**
 * ============================================================================
 * usePageNavigation
 * ============================================================================
 * 
 * 페이지 네비게이션 관련 로직 관리
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 1. 페이지 이동 핸들러 (이전/다음/특정 페이지)
 * 2. 페이지 번호 입력 UI 상태 관리
 * 3. 진행 상태 업데이트
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 사용 예시
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * const {
 *   currentPageNumber,
 *   isPageNumberExpanded,
 *   pageNumberInput,
 *   pageNumberInputRef,
 *   handlePrevPage,
 *   handleNextPage,
 *   handleNavigateToPage,
 *   handlePageNumberClick,
 *   handlePageNumberInputChange,
 *   handlePageNumberInputKeyDown,
 * } = usePageNavigation(bookUid, pageIndex, lastPageIndex, firstVisiblePageRef, lastVisiblePageRef);
 * 
 * ============================================================================
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookService } from '../../../services/bookService';

interface UsePageNavigationParams {
  bookUid: string | undefined;
  pageIndex: string | undefined;
  lastPageIndex: number;
  firstVisiblePageRef: React.MutableRefObject<number>;
  lastVisiblePageRef: React.MutableRefObject<number>;
}

export interface UsePageNavigationReturn {
  currentPageNumber: number;
  isPageNumberExpanded: boolean;
  pageNumberInput: string;
  pageNumberInputRef: React.RefObject<HTMLInputElement | null>;
  isProgressLoading: boolean;
  handlePrevPage: () => void;
  handleNextPage: () => void;
  handleNavigateToPage: (pageNo: number) => void;
  handlePageClick: (pageNo: number) => void;
  handlePageNumberClick: (e: React.MouseEvent) => void;
  handlePageNumberInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePageNumberInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const usePageNavigation = ({
  bookUid,
  pageIndex,
  lastPageIndex,
  firstVisiblePageRef,
  lastVisiblePageRef,
}: UsePageNavigationParams): UsePageNavigationReturn => {
  const navigate = useNavigate();
  
  // 페이지 번호 입력 UI 상태
  const [isPageNumberExpanded, setIsPageNumberExpanded] = useState(false);
  const [pageNumberInput, setPageNumberInput] = useState<string>('');
  const pageNumberInputRef = useRef<HTMLInputElement>(null);
  
  // 진행 상태 조회 여부 추적 (중복 호출 방지)
  const progressCheckedRef = useRef(false);
  const [isProgressLoading, setIsProgressLoading] = useState(true);
  
  // URI의 pageIndex를 그대로 사용 (URI 값과 표시 값 통일)
  const currentPageNumber = pageIndex ? parseInt(pageIndex, 10) : 0;
  
  // 첫 진입 시 항상 진행 상태 조회 및 리다이렉트
  useEffect(() => {
    if (!bookUid || progressCheckedRef.current) return;
    
    progressCheckedRef.current = true;
    setIsProgressLoading(true);
    
    bookService.getProgress(bookUid)
      .then((savedPageNo) => {
        // console.log(`[Progress] 저장된 페이지: ${savedPageNo}`);
        setIsProgressLoading(false);
        // 저장된 페이지로 리다이렉트
        navigate(`/api-book/viewer/${bookUid}/${savedPageNo}`, { replace: true });
      })
      .catch((error) => {
        // console.error('[Progress] 진행 상태 조회 실패:', error);
        setIsProgressLoading(false);
        // 실패 시 기본값 1로 리다이렉트
        navigate(`/api-book/viewer/${bookUid}/1`, { replace: true });
      });
  }, [bookUid, navigate]);
  
  // 페이지 변경 시 진행 상태 업데이트 (백업용 - 직접 핸들러에서도 업데이트하지만, 다른 경로로 변경될 경우 대비)
  const lastUpdatedPageRef = useRef<number>(0);
  useEffect(() => {
    if (!bookUid || currentPageNumber <= 0) return;
    // 이미 업데이트한 페이지는 스킵 (중복 호출 방지)
    if (lastUpdatedPageRef.current === currentPageNumber) return;
    
    lastUpdatedPageRef.current = currentPageNumber;
    
    // 진행 상태 업데이트 (비동기, 에러 무시)
    bookService.updateProgress(bookUid, currentPageNumber)
      .then(() => {
        // console.log(`[Progress] 페이지 ${currentPageNumber}로 진행 상태 업데이트 완료`);
      })
      .catch((error) => {
        // console.error('[Progress] 진행 상태 업데이트 실패:', error);
        // 실패해도 계속 진행
      });
  }, [bookUid, currentPageNumber]);
  
  // 페이지 이동 핸들러
  const handlePageClick = useCallback((pageNo: number) => {
    if (bookUid) {
      navigate(`/api-book/viewer/${bookUid}/${pageNo}`, { replace: false });
    }
  }, [bookUid, navigate]);
  
  // 네비게이션 핸들러 (원본 페이지 번호 기준으로 이동)
  const handlePrevPage = useCallback(() => {
    if (!bookUid || !pageIndex) return;
    
    // 현재 시작 페이지 이전으로 이동
    const firstPage = firstVisiblePageRef.current;
    if (firstPage <= 0) return;
    
    // 화면에 표시된 페이지 수만큼 뒤로 이동
    const displayedPages = lastVisiblePageRef.current - firstPage + 1;
    const targetPage = Math.max(1, firstPage - displayedPages);
    
    // 현재 페이지와 같으면 스킵 (무한 루프 방지)
    const currentPage = parseInt(pageIndex, 10);
    if (targetPage === currentPage) {
      // console.log(`[이전 페이지] 현재 페이지와 동일 (${currentPage}), 스킵`);
      return;
    }
    
    // console.log(`[이전 페이지] 현재 범위: ${firstPage}~${lastVisiblePageRef.current}, 이동: ${targetPage}`);
    
    // 진행 상태 업데이트
    bookService.updateProgress(bookUid, targetPage)
      .then(() => {
        // console.log(`[Progress] 페이지 ${targetPage}로 진행 상태 업데이트 완료`);
      })
      .catch((error) => {
        // console.error('[Progress] 진행 상태 업데이트 실패:', error);
      });
    
    // 강제로 리렌더링하기 위해 replace 옵션 사용
    navigate(`/api-book/viewer/${bookUid}/${targetPage}`, { replace: false });
  }, [bookUid, pageIndex, navigate, firstVisiblePageRef, lastVisiblePageRef]);
  
  const handleNextPage = useCallback(() => {
    if (!bookUid || !pageIndex) return;
    
    // 마지막으로 표시된 페이지의 다음 페이지로 이동
    const nextStartPage = lastVisiblePageRef.current + 1;
    
    // 현재 페이지와 같으면 스킵 (무한 루프 방지)
    const currentPage = parseInt(pageIndex, 10);
    if (nextStartPage === currentPage) {
      // console.log(`[다음 페이지] 현재 페이지와 동일 (${currentPage}), 스킵`);
      return;
    }
    
    // console.log(`[다음 페이지] 현재 범위: ${firstVisiblePageRef.current}~${lastVisiblePageRef.current}, 이동: ${nextStartPage}`);
    
    // 진행 상태 업데이트
    bookService.updateProgress(bookUid, nextStartPage)
      .then(() => {
        // console.log(`[Progress] 페이지 ${nextStartPage}로 진행 상태 업데이트 완료`);
      })
      .catch((error) => {
        // console.error('[Progress] 진행 상태 업데이트 실패:', error);
      });
    
    // 강제로 리렌더링하기 위해 replace 옵션 사용
    navigate(`/api-book/viewer/${bookUid}/${nextStartPage}`, { replace: false });
  }, [bookUid, pageIndex, navigate, firstVisiblePageRef, lastVisiblePageRef]);
  
  // 특정 페이지로 이동 (동기화용)
  const handleNavigateToPage = useCallback((pageNo: number) => {
    if (!bookUid) return;

    const targetPath = `/api-book/viewer/${bookUid}/${pageNo}`;
    const isSamePage = currentPageNumber === pageNo;

    // 먼저 페이지 이동 (즉시 UI 반영)
    // 같은 페이지일 때 state를 넣어 React Router가 location 변경을 인식하도록 함 (카드 클릭 시 동일 페이지에서도 이동 처리)
    navigate(targetPath, { replace: isSamePage, state: isSamePage ? { forceNavigate: Date.now() } : undefined });
    
    // 진행 상태 업데이트 (백그라운드)
    bookService.updateProgress(bookUid, pageNo)
      .then(() => {
        // console.log(`[Progress] 페이지 ${pageNo}로 진행 상태 업데이트 완료`);
      })
      .catch((error) => {
        // console.error('[Progress] 진행 상태 업데이트 실패:', error);
      });
  }, [bookUid, navigate, currentPageNumber]);
  
  // 페이지 번호 입력 UI 핸들러
  const handlePageNumberClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const target = e.target as HTMLElement;
    // 입력 필드 클릭 시에는 확장/축소하지 않음
    if (target.tagName === 'INPUT') {
      return;
    }
    
    if (!isPageNumberExpanded) {
      setIsPageNumberExpanded(true);
      // span에 표시된 범위의 앞 값(첫 페이지)을 기본값으로 사용
      const firstPage = firstVisiblePageRef.current > 0 ? firstVisiblePageRef.current : currentPageNumber;
      setPageNumberInput(firstPage.toString());
      // 다음 렌더링 사이클에서 포커스
      setTimeout(() => {
        pageNumberInputRef.current?.focus();
        pageNumberInputRef.current?.select();
      }, 0);
    } else {
      // 확장된 상태에서 버튼 클릭 시 페이지 이동
      const pageNum = parseInt(pageNumberInput, 10);
      if (!isNaN(pageNum) && pageNum > 0 && pageNum <= lastPageIndex) {
        handleNavigateToPage(pageNum);
        setIsPageNumberExpanded(false);
        setPageNumberInput('');
      } else {
        // 유효하지 않은 페이지 번호인 경우 닫기
        setIsPageNumberExpanded(false);
        setPageNumberInput('');
      }
    }
  };
  
  const handlePageNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setPageNumberInput(value);
  };
  
  const handlePageNumberInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const pageNum = parseInt(pageNumberInput, 10);
      if (!isNaN(pageNum) && pageNum > 0 && pageNum <= lastPageIndex) {
        handleNavigateToPage(pageNum);
        setIsPageNumberExpanded(false);
        setPageNumberInput('');
      }
    } else if (e.key === 'Escape') {
      setIsPageNumberExpanded(false);
      setPageNumberInput('');
    }
  };
  
  // 페이지 번호 입력 UI 외부 클릭 시 닫기
  useEffect(() => {
    if (!isPageNumberExpanded) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-page-number-indicator]')) {
        setIsPageNumberExpanded(false);
        setPageNumberInput('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPageNumberExpanded]);
  
  return {
    currentPageNumber,
    isPageNumberExpanded,
    pageNumberInput,
    pageNumberInputRef,
    isProgressLoading,
    handlePrevPage,
    handleNextPage,
    handleNavigateToPage,
    handlePageClick,
    handlePageNumberClick,
    handlePageNumberInputChange,
    handlePageNumberInputKeyDown,
  };
};
